import { Calendar, type CalendarProps } from 'primereact/calendar'
import { FloatLabel } from 'primereact/floatlabel'
import { InputMask, type InputMaskChangeEvent } from 'primereact/inputmask'
import { useEffect, useRef, useState, type CSSProperties } from 'react'

import './InputCalendar.css'

interface ExtendedCalendarProps extends InputCalendarProps {
  name?: string
  value?: Date | null
}

export interface InputCalendarProps extends CalendarProps {
  labelProps: {
    text?: string
    labelStyle?: CSSProperties
  }
}
export function RegistryCalendar({ labelProps, ...calendarProps }: InputCalendarProps) {
  const { value, onChange, onBlur, className, name, ...restCalendarProps } = calendarProps as ExtendedCalendarProps
  const calendarRef = useRef<Calendar | null>(null)

  // Tipo semplificato per adattare la chiamata onChange del Calendar quando usiamo input manuale
  interface CalendarValueEvent {
    value: Date | null
  }

  const formatDate = (date?: Date | null) => {
    if (!date || isNaN(date.getTime())) return ''
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yyyy = String(date.getFullYear())
    return `${dd}/${mm}/${yyyy}`
  }

  const parseDate = (value: string): Date | null => {
    const cleaned = value.trim()
    if (!cleaned) return null
    // Supporta separatori / - .
    const parts = cleaned.split(/[/. -]/) // separatori / . - spazio
    if (parts.length !== 3) return null
    const [d, m, y] = parts
    if (d.length !== 2 || m.length !== 2 || y.length < 4) return null
    const day = Number(d)
    const month = Number(m) - 1
    const year = Number(y)
    const candidate = new Date(year, month, day)
    if (candidate.getFullYear() !== year || candidate.getMonth() !== month || candidate.getDate() !== day) return null
    return candidate
  }

  const [textValue, setTextValue] = useState<string>(() => formatDate(value))
  const [isTyping, setIsTyping] = useState(false)

  // Calcola la data corrente: se null o invalida, usa la data di oggi per il calendario
  const currentDate: Date = (() => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return value
    }
    // Se la data è null o invalida, usa oggi come default per il calendario
    return new Date()
  })()

  // Sync esterno -> interno se cambia prop value (solo se non sta scrivendo)
  useEffect(() => {
    if (!isTyping) {
      setTextValue(formatDate(value instanceof Date && !isNaN(value.getTime()) ? value : null))
    }
  }, [value, isTyping])

  const handleTextChange = (e: InputMaskChangeEvent) => {
    const v = e.value ?? ''
    setTextValue(v)
    setIsTyping(true)
    const parsed = parseDate(v)
    if (onChange) {
      const dispatch = onChange as unknown as (e: CalendarValueEvent) => void
      if (parsed) {
        dispatch({ value: parsed })
        setIsTyping(false)
      } else {
        // Se la data non è valida o è vuota, passa null
        dispatch({ value: null })
      }
    }
  }

  const handleCalendarChange = (e: { value?: Date | null }) => {
    const date: Date | null = e.value ?? null
    setTextValue(formatDate(date))
    if (onChange) {
      ;(onChange as unknown as (e: CalendarValueEvent) => void)({ value: date })
    }
  }

  const openCalendar = () => {
    // Prova metodo show(), fallback focus se non disponibile
    const cal = calendarRef.current as unknown as { show?: () => void; input?: HTMLInputElement }
    if (cal?.show) {
      cal.show()
      // Dopo l'apertura, trova e focalizza la data selezionata o la data di oggi nel calendario
      setTimeout(() => {
        const calendarPanel = document.querySelector('.registry-calendar-overlay')
        if (calendarPanel) {
          // Cerca prima la data selezionata
          const selectedDate = calendarPanel.querySelector('.p-highlight') as HTMLElement
          if (selectedDate) {
            selectedDate.focus()
          } else {
            // Altrimenti focalizza la data di oggi se presente
            const todayDate = calendarPanel.querySelector('.p-datepicker-today > span') as HTMLElement
            if (todayDate) {
              todayDate.focus()
            } else {
              // Altrimenti focalizza il primo giorno disponibile
              const firstDate = calendarPanel.querySelector('td:not(.p-datepicker-other-month) span') as HTMLElement
              if (firstDate) {
                firstDate.focus()
              }
            }
          }
        }
      }, 100)
    } else if (cal?.input) {
      cal.input.focus()
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsTyping(false)
    if (onBlur) {
      onBlur(e)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab') {
      console.log('Tab pressed in InputCalendar')
      const input = e.target as HTMLInputElement
      const cursorPos = input.selectionStart ?? 0

      // Se sono nel giorno (0-2), vai al mese (posizione 3)
      if (cursorPos < 3) {
        e.preventDefault()
        setTimeout(() => {
          input.setSelectionRange(3, 3)
        }, 0)
      }
      // Se sono nel mese (3-5), vai all'anno (posizione 6)
      else if (cursorPos >= 3 && cursorPos < 6) {
        e.preventDefault()
        setTimeout(() => {
          input.setSelectionRange(6, 6)
        }, 0)
      }
      // Se sono nell'anno (>= 6), lascia che Tab esca normalmente
    }
  }

  return (
    <>
      <FloatLabel className={textValue ? 'p-float-label-active' : ''}>
        <div className={`relative flex w-full items-center ${textValue ? 'p-filled' : ''}`}>
          <InputMask
            id={labelProps.text}
            name={name}
            mask='99/99/9999'
            value={textValue}
            onChange={handleTextChange}
            onKeyDownCapture={handleKeyDown}
            onBlur={handleBlur}
            className={`flex-1 rounded-none !border-0 !border-x-0 !border-b !border-t-0 border-solid !border-[#D9D9D9] bg-transparent p-3 pr-12 ${
              className?.includes('p-invalid') ? 'p-invalid' : ''
            } ${textValue ? 'p-filled' : ''}`}
            autoComplete='off'
          />
          <button
            type='button'
            aria-label='Apri calendario'
            className='absolute right-3 z-10 pb-3 pt-3 text-[--primary-color]'
            onClick={openCalendar}
            onFocus={() => {
              if (!value || (value instanceof Date && isNaN(value.getTime()))) {
                openCalendar()
              }
            }}
          >
            <i className='pi pi-calendar' />
          </button>
        </div>
        <label htmlFor={labelProps.text} className={className?.includes('p-invalid') ? 'text-red-500' : ''}>
          {labelProps.text}
        </label>
      </FloatLabel>

      <Calendar
        ref={calendarRef}
        {...restCalendarProps}
        value={currentDate}
        onChange={handleCalendarChange}
        showIcon={false}
        panelClassName='registry-calendar-overlay'
        appendTo='self'
        inputId={labelProps.text + '_hidden'}
        inputClassName='hidden'
      />
    </>
  )
}
